/** Waveform lane: wavesurfer.js from the waveform peaks media, else decoded from the preview audio (on request in
 * agent mode, to spare bots a full download). The In/Out region can be dragged; its numeric twins are the In/Out
 * inputs and the ±frame/±beat buttons. */
import { useEffect, useRef, useState } from "react";
import { mediaUrl } from "../../api/client";
import { isAgentMode } from "../../lib/agent";
import type { FrameController } from "../player/controller";
import type { InOut } from "../player/Player";
import { parsePeaks } from "./peaks";
import type { View } from "./project";

type WS = import("wavesurfer.js").default;
type RegionsPlugin = import("wavesurfer.js/dist/plugins/regions.esm.js").default;

export function WaveformLane({ peaksMediaId, audioMediaId, durationMs, view, ctrl, inOut }: {
  peaksMediaId: number | null;
  audioMediaId: number | null;
  durationMs: number;
  view: View;
  ctrl: FrameController;
  inOut?: InOut;
}) {
  const box = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WS | null>(null);
  const regionsRef = useRef<RegionsPlugin | null>(null);
  const [status, setStatus] = useState<string>("");
  const [ready, setReady] = useState(false);
  const [useAudio, setUseAudio] = useState(() => !isAgentMode());
  const source = peaksMediaId != null ? "peaks" : audioMediaId != null && useAudio ? "audio" : null;
  // `inOut.set` changes identity on every URL write (the playhead writes t=): read it through a ref so the
  // wavesurfer instance is built once per source, not on every tick
  const setInOutRef = useRef(inOut?.set);
  setInOutRef.current = inOut?.set;

  useEffect(() => {
    if (!source || !box.current) return;
    let cancelled = false;
    let ws: WS | null = null;
    setReady(false);
    setStatus("Loading waveform…");
    (async () => {
      const { default: WaveSurfer } = await import("wavesurfer.js");
      const { default: Regions } = await import("wavesurfer.js/dist/plugins/regions.esm.js");
      let peaks: number[][] | undefined;
      if (source === "peaks") {
        const r = await fetch(mediaUrl(peaksMediaId as number), { credentials: "same-origin" });
        if (!r.ok) throw new Error(`peaks HTTP ${r.status}`);
        peaks = parsePeaks(await r.json()) ?? undefined;
        if (!peaks) throw new Error("unknown peaks format");
      }
      if (cancelled || !box.current) return;
      const css = getComputedStyle(document.documentElement);
      const color = (v: string, d: string) => css.getPropertyValue(v).trim() || d;
      ws = WaveSurfer.create({
        container: box.current, height: 48, normalize: true, interact: true, autoScroll: false, autoCenter: false,
        hideScrollbar: true, cursorWidth: 0, waveColor: color("--muted", "#666"), progressColor: color("--muted", "#666"),
        peaks, duration: durationMs / 1000, url: source === "audio" ? mediaUrl(audioMediaId as number) : undefined,
      });
      const regions = ws.registerPlugin(Regions.create());
      wsRef.current = ws;
      regionsRef.current = regions;
      ws.on("interaction", (t: number) => ctrl.seekMs(t * 1000));
      ws.on("ready", () => {
        if (cancelled) return;
        setReady(true);
        setStatus(source === "peaks" ? "Waveform from peaks" : "Waveform from the preview audio");
      });
      ws.on("error", (e: Error) => setStatus(`Waveform unavailable: ${e.message}`));
      regions.on("region-updated", (r) => {
        if (r.id === "inout") setInOutRef.current?.({ in: Math.round(r.start * 1000), out: Math.round(r.end * 1000) });
      });
    })().catch((e: unknown) => {
      if (!cancelled) setStatus(`Waveform unavailable: ${(e as Error).message}`);
    });
    return () => {
      cancelled = true;
      ws?.destroy();
      wsRef.current = null;
      regionsRef.current = null;
    };
  }, [source, peaksMediaId, audioMediaId, durationMs, ctrl]);

  // Keep the waveform viewport on the lanes' view.
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || !ready || !box.current) return;
    const spanS = (view.v1 - view.v0) / 1000;
    const width = box.current.clientWidth || 1;
    try {
      ws.zoom(spanS * 1000 >= durationMs ? 0 : width / spanS);
      ws.setScrollTime(view.v0 / 1000);
    } catch {
      /* zoom before decode: ignore */
    }
  }, [view, ready, durationMs]);

  // In/Out region mirrors the URL values.
  const inMs = inOut?.inMs ?? null;
  const outMs = inOut?.outMs ?? null;
  useEffect(() => {
    const regions = regionsRef.current;
    if (!regions || !ready) return;
    regions.clearRegions();
    if (inMs != null && outMs != null && outMs > inMs) {
      regions.addRegion({ id: "inout", start: inMs / 1000, end: outMs / 1000, drag: true, resize: true,
        color: "rgba(36, 87, 214, 0.18)" });
    }
  }, [inMs, outMs, ready]);

  return (
    <div className="ws8-lane" role="group" aria-label="Waveform lane" data-testid="lane-wave">
      <div className="ws8-lane-label" aria-hidden="true">Waveform</div>
      <div className="ws8-track ws8-wave">
        <div ref={box} className="ws8-wave-box" aria-hidden="true" />
        {!source && (
          <span className="ws8-wave-msg">
            {audioMediaId == null ? "No waveform: no peaks or preview audio." : (
              <button type="button" data-testid="load-waveform" onClick={() => setUseAudio(true)}>
                Load waveform from preview audio
              </button>
            )}
          </span>
        )}
        {source && status && (
          <span className={ready ? "ws8-sr-only" : "ws8-wave-msg"} data-testid="waveform-status">{status}</span>
        )}
      </div>
    </div>
  );
}
